/*
 * The overview.
 *
 * The tiles are not vanity metrics. Each of the three under "needs attention" is a
 * state the data model makes possible and which is easy to forget about, and each
 * is a link to the filter that finds them:
 *
 *   unpublished drafts   someone edited a live article and did not publish it, so a
 *                        revision sits ahead of the one readers get
 *   unwritten            the article row exists with no revision at all, which the
 *                        LEFT JOIN in kb.ArticleDetail is there to allow
 *   untagged             "at least one tag" is an application rule the database
 *                        cannot enforce, so it needs watching
 *
 * Its words come from the chosen language's dictionary, `t.admin.shell.overview`
 * (S48, 2026-10-29).
 */

import { Link } from 'react-router-dom';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAsync } from '../hooks/useAsync.js';
import { DateTime, ErrorBanner, Loading } from '../components/ui.js';
import { useMessages } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';

export function DashboardPage(): JSX.Element {
  const api = useAdminApi();
  const state = useAsync(() => api.overview(), []);
  const t = useMessages().admin.shell.overview;

  if (state.loading && !state.data) return <Loading label={t.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading />;

  const overview = state.data;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{t.title}</h1>
          <p>{t.intro}</p>
        </div>
        <Link className="button button-primary" to="/admin/articles/new">
          {t.newArticle}
        </Link>
      </div>

      <div className="stat-grid">
        <Stat label={t.articles} value={overview.articles} to="/admin/articles" />
        <Stat label={t.live} value={overview.liveArticles} to="/admin/articles?status=live" />
        <Stat
          label={t.scheduled}
          value={overview.scheduledArticles}
          to="/admin/articles?status=scheduled"
        />
        <Stat label={t.hidden} value={overview.hiddenArticles} to="/admin/articles?status=hidden" />
      </div>

      <h2 style={{ marginTop: '2rem' }}>{t.needsAttention}</h2>
      <div className="stat-grid">
        <Stat
          label={t.unpublishedDrafts}
          value={overview.unpublishedDrafts}
          to="/admin/articles?status=draft"
          attention={overview.unpublishedDrafts > 0}
          hint={t.unpublishedDraftsHint}
        />
        <Stat
          label={t.withdrawn}
          value={overview.withdrawnArticles}
          to="/admin/articles?status=withdrawn"
          attention={overview.withdrawnArticles > 0}
          hint={t.withdrawnHint}
        />
        <Stat
          label={t.unwritten}
          value={overview.unwrittenArticles}
          to="/admin/articles?status=unwritten"
          attention={overview.unwrittenArticles > 0}
          hint={t.unwrittenHint}
        />
        <Stat
          label={t.untagged}
          value={overview.untaggedArticles}
          to="/admin/articles?status=untagged"
          attention={overview.untaggedArticles > 0}
          hint={t.untaggedHint}
        />
      </div>

      <div className="split-two" style={{ marginTop: '2rem' }}>
        <div className="card">
          <div className="card-header">
            <h2>{t.recentlyEdited}</h2>
          </div>
          {overview.recentlyModified.length === 0 ? (
            <p className="muted">{t.nothingEdited}</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {overview.recentlyModified.map((item) => (
                <li
                  key={item.articleId}
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <Link to={`/admin/articles/${item.articleId}`}>{item.title}</Link>
                  <span className="faint" style={{ whiteSpace: 'nowrap' }}>
                    <DateTime value={item.modifiedAtUtc} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="card">
            <div className="card-header">
              <h3>{t.nextScheduled}</h3>
            </div>
            {overview.nextScheduled ? (
              <>
                <p style={{ marginBottom: '0.25rem' }}>
                  <Link to={`/admin/articles/${overview.nextScheduled.articleId}`}>
                    {overview.nextScheduled.title}
                  </Link>
                </p>
                <p className="faint" style={{ margin: 0 }}>
                  {richText(t.goesLive, { date: <DateTime value={overview.nextScheduled.publishAtUtc} /> })}
                </p>
              </>
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                {t.nothingScheduled}
              </p>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3>{t.metadata}</h3>
            </div>
            <p className="muted" style={{ margin: 0 }}>
              {t.tagCounts(overview.products, overview.categories)}
            </p>
            <p style={{ marginTop: '0.75rem', marginBottom: 0 }}>
              <Link to="/admin/metadata">{t.manageMetadata}</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  to,
  hint,
  attention,
}: {
  label: string;
  value: number;
  to: string;
  hint?: string;
  attention?: boolean;
}): JSX.Element {
  return (
    <Link
      to={to}
      className={`stat${attention ? ' stat-attention' : ''}`}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      title={hint}
    >
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </Link>
  );
}
